import React, { useState, useRef, useMemo, useEffect } from 'react';
import { ConfigData, Department, StaffMember } from '../lib/types';
import {
  updateConfig, addStaff, importStaff, deactivateStaff,
  addDepartment, deactivateDepartment, updateCategories,
  updateCategoryBudgets, inviteUser,
  getVendors, addVendor, toggleVendor,
} from '../lib/api';
import type { Vendor } from '../lib/types';
import { ConfirmModal } from './ui';
import { inp, lbl, useFlash } from './settings/shared';
import { SettingsGeneral } from './settings/SettingsGeneral';
import { SettingsDepartments } from './settings/SettingsDepartments';
import { SettingsCategories } from './settings/SettingsCategories';
import { SettingsTeam } from './settings/SettingsTeam';
import { SettingsVendors } from './settings/SettingsVendors';

interface SettingsTabProps {
  configData: ConfigData | null;
  locale: string;
  onRefresh?: () => void;
}

type SettingsSection = 'general' | 'departments' | 'categories' | 'team' | 'vendors';

export function SettingsTab({ configData, locale, onRefresh }: SettingsTabProps) {
  const userRole = configData?.profile?.role || 'dept_admin';
  const isOrgAdmin = ['org_admin', 'super_admin'].includes(userRole);
  const isAdmin = isOrgAdmin || userRole === 'dept_admin';
  const flash = useFlash();

  const [activeSection, setActiveSection] = useState<SettingsSection>('general');

  // General
  const [orgName, setOrgName]       = useState(configData?.config?.org_name || '');
  const [currency, setCurrency]     = useState(configData?.config?.currency_symbol || '$');
  const [localeVal, setLocaleVal]   = useState(locale);
  const [threshold, setThreshold]   = useState(String(configData?.config?.low_stock_threshold || 5));
  const [saved, setSaved]           = useState(false);
  const [saveError, setSaveError]   = useState('');

  // Departments
  const [newDept, setNewDept] = useState('');
  const [deptMsg, setDeptMsg] = useState('');
  const [deptBusy, setDeptBusy] = useState(false);

  // Categories
  const [categories, setCategories] = useState<string[]>(configData?.categories || []);
  const [newCat, setNewCat]         = useState('');
  const [catMsg, setCatMsg]         = useState('');
  const [catBusy, setCatBusy]       = useState(false);
  const initBudgets = configData?.category_budgets || { PPE: 5000, Consumable: 3000 };
  const [categoryBudgets, setCategoryBudgets] = useState<Record<string, number>>(initBudgets);

  // Team
  const [invEmail, setInvEmail]   = useState('');
  const [invName, setInvName]     = useState('');
  const [invDepts, setInvDepts]   = useState<string[]>([]);
  const [invRole, setInvRole]     = useState('dept_admin');
  const [invMsg, setInvMsg]       = useState('');
  const [invBusy, setInvBusy]     = useState(false);
  const [newStaffName, setNewStaffName]   = useState('');
  const [newStaffEmail, setNewStaffEmail] = useState('');
  const [newStaffDept, setNewStaffDept]   = useState('');
  const [staffMsg, setStaffMsg]           = useState('');
  const [staffBusy, setStaffBusy]         = useState(false);
  const [staffSearch, setStaffSearch]     = useState('');
  const [staffPage, setStaffPage]         = useState(1);
  const csvRef = useRef<HTMLInputElement>(null);

  // Vendors
  const [vendors, setVendors]             = useState<Vendor[]>([]);
  const [vendorsLoading, setVendorsLoading] = useState(false);
  const [newVendorName, setNewVendorName] = useState('');
  const [newVendorDomain, setNewVendorDomain] = useState('');
  const [vendorMsg, setVendorMsg]         = useState('');
  const [vendorBusy, setVendorBusy]       = useState(false);

  // Confirm modal (deactivate dept / staff, remove category)
  const [confirm, setConfirm] = useState<{ title: string; message: React.ReactNode; onConfirm: () => void; destructive?: boolean } | null>(null);

  const activeDepts: Department[]   = (configData?.departments ?? []).filter(d => d.active !== false);
  const activeStaff: StaffMember[]  = (configData?.directory ?? []).filter(s => s.active !== false);

  const filteredStaff = useMemo(() => {
    if (!staffSearch.trim()) return activeStaff;
    const q = staffSearch.toLowerCase();
    return activeStaff.filter(s =>
      s.name?.toLowerCase().includes(q) ||
      s.email?.toLowerCase().includes(q) ||
      s.role?.toLowerCase().includes(q)
    );
  }, [activeStaff, staffSearch]);

  useEffect(() => {
    if (!isAdmin) return;
    setVendorsLoading(true);
    getVendors().then(result => {
      if (result.ok && result.data) setVendors(result.data as Vendor[]);
      setVendorsLoading(false);
    });
  }, [isAdmin]);

  const loadVendors = async () => {
    const result = await getVendors();
    if (result.ok && result.data) setVendors(result.data as Vendor[]);
  };

  // ── Handlers ───────────────────────────────────────────────────
  const handleSaveGeneral = async () => {
    setSaveError('');
    try {
      const result = await updateConfig({
        org_name: orgName, currency_symbol: currency, locale: localeVal,
        low_stock_threshold: Number(threshold),
      });
      if (result && !result.ok) { setSaveError(result.message || 'Failed to save'); return; }
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      onRefresh?.();
    } catch { setSaveError('Failed to save settings'); }
  };

  const handleAddDept = async () => {
    if (!newDept.trim()) return;
    setDeptBusy(true);
    const result = await addDepartment(newDept.trim());
    if (result.ok) { flash(setDeptMsg, '✓ ' + newDept.trim() + ' added'); setNewDept(''); onRefresh?.(); }
    else { flash(setDeptMsg, '⚠ ' + (result.error || 'Failed')); }
    setDeptBusy(false);
  };

  const handleDeactivateDept = (id: string, name: string) => {
    setConfirm({
      title: 'Deactivate department',
      message: <>Deactivate <strong>{name}</strong>?</>,
      onConfirm: async () => {
        const result = await deactivateDepartment(id);
        if (result.ok) { flash(setDeptMsg, '✓ ' + name + ' deactivated'); onRefresh?.(); }
        else { flash(setDeptMsg, '⚠ ' + (result.error || 'Failed')); }
      },
    });
  };

  const handleAddCategory = async () => {
    const cat = newCat.trim();
    if (!cat || categories.includes(cat)) return;
    setCatBusy(true);
    const updated = [...categories, cat];
    const result = await updateCategories(updated);
    if (result.ok) {
      setCategories(updated);
      const newBudgets = { ...categoryBudgets, [cat]: 0 };
      setCategoryBudgets(newBudgets);
      await updateCategoryBudgets(newBudgets);
      setNewCat('');
      flash(setCatMsg, '✓ "' + cat + '" added');
      onRefresh?.();
    } else { flash(setCatMsg, '⚠ ' + (result.message || result.error || 'Failed')); }
    setCatBusy(false);
  };

  const handleRemoveCategory = (cat: string) => {
    if (categories.length <= 1) { flash(setCatMsg, '⚠ Must have at least one category'); return; }
    setConfirm({
      title: 'Remove category',
      message: <>Remove <strong>{cat}</strong>?</>,
      destructive: true,
      onConfirm: async () => {
        setCatBusy(true);
        const updated = categories.filter(c => c !== cat);
        const result = await updateCategories(updated);
        if (result.ok) {
          setCategories(updated);
          const newBudgets = { ...categoryBudgets }; delete newBudgets[cat];
          setCategoryBudgets(newBudgets);
          await updateCategoryBudgets(newBudgets);
          flash(setCatMsg, '✓ "' + cat + '" removed');
          onRefresh?.();
        } else { flash(setCatMsg, '⚠ ' + (result.message || result.error || 'Failed')); }
        setCatBusy(false);
      },
    });
  };

  const handleBudgetChange = (cat: string, value: string) => {
    setCategoryBudgets(prev => ({ ...prev, [cat]: Number(value) || 0 }));
  };

  const handleSaveBudgets = async () => {
    setCatBusy(true);
    await updateCategoryBudgets(categoryBudgets);
    flash(setCatMsg, '✓ Budgets saved');
    setCatBusy(false);
  };

  const handleInvite = async () => {
    if (!invEmail.trim()) return;
    if (invRole === 'dept_admin' && invDepts.length === 0) {
      flash(setInvMsg, '⚠ Please select at least one department');
      return;
    }
    setInvBusy(true);
    const result = await inviteUser({
      email: invEmail.trim(), display_name: invName.trim() || undefined,
      department_ids: invDepts.length > 0 ? invDepts : undefined,
      role: invRole || 'dept_admin',
    });
    if (result.ok) {
      flash(setInvMsg, '✓ Invitation sent to ' + invEmail.trim());
      setInvEmail(''); setInvName(''); setInvDepts([]); setInvRole('dept_admin');
      onRefresh?.();
    } else { flash(setInvMsg, '⚠ ' + (result.message || result.error || 'Failed')); }
    setInvBusy(false);
  };

  const handleAddStaff = async () => {
    if (!newStaffName.trim()) return;
    setStaffBusy(true);
    const result = await addStaff({
      full_name: newStaffName.trim(),
      department_id: newStaffDept || undefined,
      email: newStaffEmail.trim() || undefined,
    });
    if (result.ok) {
      flash(setStaffMsg, '✓ ' + newStaffName.trim() + ' added');
      setNewStaffName(''); setNewStaffEmail(''); setNewStaffDept('');
      onRefresh?.();
    } else { flash(setStaffMsg, '⚠ ' + (result.error || 'Failed')); }
    setStaffBusy(false);
  };

  const handleDeactivateStaff = (id: string, name: string) => {
    setConfirm({
      title: 'Deactivate staff member',
      message: <>Deactivate <strong>{name}</strong>?</>,
      onConfirm: async () => {
        const result = await deactivateStaff(id);
        if (result.ok) { flash(setStaffMsg, '✓ ' + name + ' deactivated'); onRefresh?.(); }
        else { flash(setStaffMsg, '⚠ ' + (result.error || 'Failed')); }
      },
    });
  };

  const handleCSVImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setStaffBusy(true); setStaffMsg('');
    try {
      const text = await file.text();
      const lines = text.split('\n').map(l => l.trim()).filter(l => l);
      if (lines.length === 0) { flash(setStaffMsg, '⚠ Empty file'); setStaffBusy(false); return; }
      let startIdx = 0;
      if (lines[0].toLowerCase().includes('name')) startIdx = 1;
      const staffList = lines.slice(startIdx).map(line => {
        const cols = line.split(',').map(c => c.trim().replace(/^"|"$/g, ''));
        return { full_name: cols[0] || '', short_name: cols[1] || '', department: cols[2] || '', email: cols[3] || '', role: cols[4] || '' };
      }).filter(s => s.full_name);
      if (staffList.length === 0) { flash(setStaffMsg, '⚠ No valid rows'); setStaffBusy(false); return; }
      const result = await importStaff(staffList);
      if (result.ok) { flash(setStaffMsg, '✓ ' + staffList.length + ' staff imported'); onRefresh?.(); }
      else { flash(setStaffMsg, '⚠ ' + (result.error || 'Import failed')); }
    } catch (err) { flash(setStaffMsg, '⚠ ' + (err instanceof Error ? err.message : 'Error')); }
    setStaffBusy(false);
    if (csvRef.current) csvRef.current.value = '';
  };

  const handleAddVendor = async () => {
    if (!newVendorName.trim() || !newVendorDomain.trim()) return;
    setVendorBusy(true);
    const result = await addVendor({ name: newVendorName.trim(), domain: newVendorDomain.trim() });
    if (result.ok) { flash(setVendorMsg, '✓ ' + newVendorName.trim() + ' added'); setNewVendorName(''); setNewVendorDomain(''); await loadVendors(); }
    else { flash(setVendorMsg, '⚠ ' + (result.message || result.error || 'Failed')); }
    setVendorBusy(false);
  };

  const handleToggleVendor = async (id: string, currentEnabled: boolean) => {
    const result = await toggleVendor(id, !currentEnabled);
    if (result.ok) { setVendors(prev => prev.map(v => v.id === id ? { ...v, enabled: !currentEnabled } : v)); }
    else { flash(setVendorMsg, '⚠ ' + (result.message || result.error || 'Failed')); }
  };

  const sideItem = (section: SettingsSection, label: string) => (
    <button key={section} onClick={() => setActiveSection(section)}
      style={{
        display: 'block', width: '100%', padding: '9px 16px', fontSize: 12, fontFamily: 'var(--font)',
        background: activeSection === section ? 'var(--accent-bg)' : 'none',
        color: activeSection === section ? 'var(--accent)' : 'var(--text2)',
        fontWeight: activeSection === section ? 600 : 400,
        border: 'none', borderLeft: activeSection === section ? '2px solid var(--accent)' : '2px solid transparent',
        cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s ease',
      }}>{label}</button>
  );

  if (!isAdmin) {
    return (
      <div className="pane active" style={{ maxWidth: 600 }}>
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg, 16px)', padding: 20 }}>
          <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 16 }}>Organisation</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div><label style={lbl}>Name</label><div style={{ ...inp, background: 'var(--bg)', cursor: 'default' }}>{orgName || '—'}</div></div>
            <div><label style={lbl}>Currency</label><div style={{ ...inp, background: 'var(--bg)', cursor: 'default' }}>{currency}</div></div>
            <div><label style={lbl}>Locale</label><div style={{ ...inp, background: 'var(--bg)', cursor: 'default' }}>{locale}</div></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pane active">
      <div style={{ display: 'flex', gap: 0, background: 'var(--surface)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg, 16px)', overflow: 'hidden', minHeight: 480, boxShadow: 'var(--shadow-sm)' }}>

        <div style={{ width: 180, borderRight: '1px solid var(--border)', padding: '12px 0', flexShrink: 0, background: 'var(--bg)' }}>
          {sideItem('general', 'General')}
          {sideItem('departments', 'Departments')}
          {sideItem('categories', 'Categories')}
          {sideItem('team', 'Team')}
          {sideItem('vendors', 'Vendors')}
        </div>

        <div style={{ flex: 1, padding: '20px 28px', overflow: 'auto' }}>
          {activeSection === 'general' && (
            <SettingsGeneral
              orgName={orgName} setOrgName={setOrgName}
              currency={currency} setCurrency={setCurrency}
              localeVal={localeVal} setLocaleVal={setLocaleVal}
              threshold={threshold} setThreshold={setThreshold}
              saved={saved} saveError={saveError}
              onSave={handleSaveGeneral}
            />
          )}
          {activeSection === 'departments' && (
            <SettingsDepartments
              activeDepts={activeDepts}
              newDept={newDept} setNewDept={setNewDept}
              deptMsg={deptMsg} deptBusy={deptBusy}
              onAdd={handleAddDept}
              onDeactivate={handleDeactivateDept}
            />
          )}
          {activeSection === 'categories' && (
            <SettingsCategories
              categories={categories}
              currency={currency}
              categoryBudgets={categoryBudgets}
              newCat={newCat} setNewCat={setNewCat}
              catMsg={catMsg} catBusy={catBusy}
              onAdd={handleAddCategory}
              onRemove={handleRemoveCategory}
              onBudgetChange={handleBudgetChange}
              onSaveBudgets={handleSaveBudgets}
            />
          )}
          {activeSection === 'team' && (
            <SettingsTeam
              isOrgAdmin={isOrgAdmin}
              activeDepts={activeDepts}
              activeStaff={activeStaff}
              filteredStaff={filteredStaff}
              userCount={configData?.user_count}
              maxUsers={configData?.max_users}
              orgName={configData?.config?.org_name}
              invEmail={invEmail} setInvEmail={setInvEmail}
              invName={invName} setInvName={setInvName}
              invDepts={invDepts} setInvDepts={setInvDepts}
              invRole={invRole} setInvRole={setInvRole}
              invBusy={invBusy} invMsg={invMsg}
              onInvite={handleInvite}
              newStaffName={newStaffName} setNewStaffName={setNewStaffName}
              newStaffEmail={newStaffEmail} setNewStaffEmail={setNewStaffEmail}
              newStaffDept={newStaffDept} setNewStaffDept={setNewStaffDept}
              staffBusy={staffBusy} staffMsg={staffMsg}
              onAddStaff={handleAddStaff}
              csvRef={csvRef}
              onCSVImport={handleCSVImport}
              staffSearch={staffSearch}
              staffPage={staffPage} setStaffPage={setStaffPage}
              onSearchChange={(s) => { setStaffSearch(s); setStaffPage(1); }}
              onDeactivateStaff={handleDeactivateStaff}
            />
          )}
          {activeSection === 'vendors' && (
            <SettingsVendors
              vendors={vendors}
              vendorsLoading={vendorsLoading}
              newVendorName={newVendorName} setNewVendorName={setNewVendorName}
              newVendorDomain={newVendorDomain} setNewVendorDomain={setNewVendorDomain}
              vendorBusy={vendorBusy} vendorMsg={vendorMsg}
              onAdd={handleAddVendor}
              onToggle={handleToggleVendor}
            />
          )}
        </div>
      </div>

      <ConfirmModal
        open={!!confirm}
        title={confirm?.title}
        message={confirm?.message}
        destructive={confirm?.destructive}
        confirmLabel={confirm?.destructive ? 'Remove' : 'Deactivate'}
        onCancel={() => setConfirm(null)}
        onConfirm={() => { confirm?.onConfirm(); setConfirm(null); }}
      />
    </div>
  );
}
